import { FC } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment, { Moment } from 'moment';

import { BaseInputProps } from '@/components';

import { FormElementProps } from '../types';

const DATE_FORMAT = 'MM/DD/YYYY';

export interface DatePickersFormFieldProps
  extends Omit<BaseInputProps, 'name'>,
    FormElementProps {
  format?: string; // format how to emit date
  displayFormat?: string; // format how to display date
  minDate?: Moment;
  maxDate?: Moment;
  handleChange?: (name: string, value: string | number) => void;
  handleAccept?: (name: string, value: string | number) => void;
}

export const FormDateField: FC<DatePickersFormFieldProps> = (props) => {
  const {
    name,
    rules,
    defaultValue,
    handleChange,
    handleAccept,
    format = 'YYYY-MM-DD',
    displayFormat = DATE_FORMAT,
    label,
    ...rest
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;
  const isRequired = !!rules?.required;

  return (
    <Controller
      control={control}
      defaultValue={Boolean(defaultValue) || ''}
      name={name}
      render={({ field: { onChange, ...restField } }: any) => (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            slotProps={{ field: { clearable: true } }}
            {...rest}
            {...restField}
            error={error?.message}
            label={
              <Box component="span">
                {label}
                {isRequired && (
                  <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Box>
                )}
              </Box>
            }
            format={displayFormat}
            value={restField.value ? moment(restField.value) : null}
            onChange={(value: Moment | null) => {
              const formattedValue = value ? value.format(format) : null;
              onChange(formattedValue);

              if (handleAccept) handleAccept(name, formattedValue || '');
              if (handleChange) handleChange(name, formattedValue || '');
            }}
          />
        </LocalizationProvider>
      )}
      rules={rules}
    />
  );
};
